import React, { useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  Spinner,
  Badge,
  Button,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Bar,
  Line,
  LineChart,
  ReferenceLine,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import {
  ChartPie,
  CurrencyDollar,
  WarningCircle,
  TrendUp,
  Percent,
  ArrowSquareOut,
  Gauge,
} from 'phosphor-react';
import { useAuditoriaInteligente, IPeriodoParams } from '../../../../hooks/useAuditoriaInteligente';
import { formatCurrency } from '../../../../utils/FormatFunctions';
import { num, pct } from '../format';
import { IAuditoriaEmpresa, ISimples, AuditoriaArea } from '../types';
import { KpiCard } from '../components/KpiCard';
import { StatusBadge } from '../components/StatusBadge';
import { EmptyState } from '../components/EmptyState';
import { InfoBloco, FILTRO } from '../components/InfoPopover';
import { UI } from '../ui';

interface SimplesScreenProps {
  empresa: IAuditoriaEmpresa | null;
  periodo: IPeriodoParams;
  onScreen?: (screen: string) => void;
  onOpenAuditoria?: (area: AuditoriaArea, focusKey?: string) => void;
}

// Tetos do Simples Nacional (ano-calendário).
const LIMITE_SN = 4_800_000;
const SUBLIMITE = 3_600_000;

/** Cor do indicador de sublimite por faixa de risco. */
const corLimite = (p: number): string => {
  if (p >= 90) return UI.neg;
  if (p >= 75) return UI.warn;
  return UI.pos;
};

/** 'YYYY-MM' → 'MM/AA'; demais formatos passam direto. */
const mesLabel = (m: string) => {
  const mm = /^(\d{4})-(\d{2})$/.exec(m ?? '');
  return mm ? `${mm[2]}/${mm[1].slice(2)}` : m;
};

const Glass = ({
  title,
  sub,
  action,
  children,
}: {
  title: string;
  sub?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Box
    borderRadius="18px"
    p={5}
    bg="rgba(255,255,255,0.7)"
    backdropFilter="blur(14px) saturate(160%)"
    border="1px solid rgba(255,255,255,0.85)"
    boxShadow="0 12px 34px -18px rgba(15,23,42,0.35)"
  >
    <Flex justify="space-between" align="center" mb={4} gap={2}>
      <Box>
        <Text fontSize="13px" fontWeight={800} color={UI.ink}>
          {title}
        </Text>
        {sub && (
          <Text fontSize="11px" color={UI.faint} fontWeight={600}>
            {sub}
          </Text>
        )}
      </Box>
      {action}
    </Flex>
    {children}
  </Box>
);

export function SimplesScreen({ empresa, periodo, onScreen, onOpenAuditoria }: SimplesScreenProps) {
  const { getSimples } = useAuditoriaInteligente();

  const { data, isFetching } = useQuery<ISimples>({
    queryKey: ['ai-simples', { id: empresa?.id, ...periodo }],
    queryFn: getSimples,
    enabled: !!empresa,
  });

  const fatBase = useMemo(() => data?.fat_x_base ?? [], [data]);

  const resumo = useMemo(() => {
    const dasTotal = fatBase.reduce((s, m) => s + num(m.das), 0);
    const fatTotal = fatBase.reduce((s, m) => s + num(m.faturamento), 0);
    const divergencias = fatBase.filter(m => m.status === 'DIVERGENCIA').length;
    const ultimo = data?.sublimite?.[data.sublimite.length - 1];
    return {
      dasTotal,
      fatTotal,
      divergencias,
      ultimo,
      aliqEfetiva: fatTotal ? (dasTotal / fatTotal) * 100 : 0,
    };
  }, [fatBase, data]);

  const fatChart = useMemo(
    () =>
      fatBase.map(m => ({
        mes: mesLabel(m.mes),
        faturamento: num(m.faturamento),
        base: num(m.base_sn),
        das: num(m.das),
        aliq: num(m.faturamento) ? (num(m.das) / num(m.faturamento)) * 100 : 0,
      })),
    [fatBase],
  );
  const subChart = useMemo(
    () =>
      (data?.sublimite ?? []).map(s => ({
        periodo: mesLabel(s.periodo),
        acumulado: num(s.acumulado_12m),
        pct: num(s.pct_limite),
      })),
    [data],
  );

  if (!empresa) {
    return (
      <EmptyState
        title="Selecione uma empresa"
        message="Use a busca no topo (ID, CNPJ ou Razão Social) para ver o Simples Nacional."
        icon={ChartPie}
      />
    );
  }

  // Guard cadastral: se a empresa NÃO é do Simples (flag explícita ou regime de Lucro
  // Presumido/Real), bloqueia já — sem depender do `optante` que o backend pode não setar.
  const naoSimples =
    empresa.is_simples_nacional === false || /presumido|real/i.test(empresa.regime ?? '');
  if (naoSimples) {
    return (
      <EmptyState
        title="Empresa não optante pelo Simples"
        message={`Esta empresa está no regime ${empresa.regime ?? 'atual'} — a tela do Simples Nacional não se aplica a ela.`}
        icon={ChartPie}
      />
    );
  }

  if (isFetching && !data) {
    return (
      <Box textAlign="center" py={16}>
        <Spinner />
      </Box>
    );
  }

  if (data && !data.optante) {
    return (
      <EmptyState
        title="Empresa não optante pelo Simples"
        message={
          data.mensagem ??
          `Esta empresa está no regime ${data.regime ?? 'atual'} — a tela do Simples Nacional não se aplica.`
        }
        icon={ChartPie}
      />
    );
  }

  const pctLimite = num(resumo.ultimo?.pct_limite);
  const limiteColor = corLimite(pctLimite);

  // Faixa do Anexo I onde o acumulado 12m se enquadra (menor RBT12 >= acumulado),
  // calculada uma única vez — senão todas as faixas acima do acumulado se realçam.
  const acum12 = num(resumo.ultimo?.acumulado_12m);
  const faixaAtual =
    acum12 > 0
      ? [...(data?.tabela_aliquotas_anexo_i ?? [])]
          .sort((x, y) => num(x.rbt12_ate) - num(y.rbt12_ate))
          .find(r => acum12 <= num(r.rbt12_ate))?.faixa
      : undefined;

  return (
    <Box>
      {/* KPIs */}
      <SimpleGrid columns={{ base: 2, lg: 5 }} spacing={4} mb={5}>
        <KpiCard
          label="DAS no período"
          value={formatCurrency(resumo.dasTotal)}
          icon={CurrencyDollar}
          iconColor={UI.pos}
          iconBg={UI.posSoft}
          info={
            <InfoBloco
              oque="Total do DAS (Documento de Arrecadação do Simples) devido pela empresa no período selecionado."
              dados="extract_simples_nacional.valor_total (somado por competência)"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Faturamento"
          value={formatCurrency(resumo.fatTotal)}
          icon={TrendUp}
          iconColor={UI.brand}
          iconBg={UI.infoSoft}
          info={
            <InfoBloco
              oque="Faturamento do período na regra do Relatório de Faturamento do Domínio: acumuladores com IFAT = S (saídas − devoluções + serviços)."
              dados="extract_acumuladores.saldo (ifat = 'S', mensal) — mês sem extração v2 usa as notas de venda + NFS-e"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Alíquota efetiva"
          value={pct(resumo.aliqEfetiva, 2)}
          sub="DAS / faturamento"
          icon={Percent}
          iconColor={UI.warn}
          iconBg={UI.warnSoft}
          info={
            <InfoBloco
              oque="Carga tributária efetiva do período: razão entre o DAS pago e o faturamento total."
              dados="extract_simples_nacional.valor_total ÷ faturamento (regra do Domínio)"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Divergências"
          value={resumo.divergencias}
          icon={WarningCircle}
          iconColor={resumo.divergencias ? UI.neg : UI.pos}
          iconBg={resumo.divergencias ? UI.negSoft : UI.posSoft}
          onClick={onOpenAuditoria ? () => onOpenAuditoria('sn') : undefined}
          info={
            <InfoBloco
              oque="Quantidade de meses no período em que o faturamento (regra do Domínio) diverge da base declarada no Simples (diferença acima de R$ 1,00)."
              dados="faturamento (acumuladores ifat = 'S') vs. extract_simples_nacional.base (por competência)"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="% do sublimite"
          value={pct(pctLimite)}
          sub={formatCurrency(num(resumo.ultimo?.acumulado_12m))}
          icon={Gauge}
          iconColor={limiteColor}
          iconBg={`${limiteColor}1A`}
          info={
            <InfoBloco
              oque="Quão perto a empresa está do teto do Simples (R$ 4,8M): receita acumulada nos últimos 12 meses dividida pelo limite, na competência mais recente."
              dados="SUM(extract_simples_nacional.base) em janela móvel de 12 meses ÷ 4.800.000"
              filtro={FILTRO.empresa}
            />
          }
        />
      </SimpleGrid>

      {/* Faturamento x base x DAS */}
      <Glass
        title="Faturamento × base do Simples × DAS"
        sub="por mês"
        action={
          onScreen && (
            <Button
              size="xs"
              variant="ghost"
              color={UI.brand}
              rightIcon={<ArrowSquareOut size={13} weight="bold" />}
              onClick={() => onScreen('impostos-apurados')}
            >
              Impostos apurados
            </Button>
          )
        }
      >
        {fatChart.length === 0 ? (
          <Text fontSize="13px" color={UI.muted}>
            Sem dados no período.
          </Text>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={fatChart}>
              <CartesianGrid strokeDasharray="3 3" stroke={UI.border} />
              <XAxis dataKey="mes" fontSize={11} stroke={UI.muted} />
              <YAxis
                yAxisId="l"
                fontSize={11}
                stroke={UI.muted}
                width={70}
                tickFormatter={v => formatCurrency(v)}
              />
              <Tooltip formatter={(v: number) => formatCurrency(v)} />
              <Legend />
              <Bar
                yAxisId="l"
                dataKey="faturamento"
                name="Faturamento"
                fill={UI.brand}
                radius={[4, 4, 0, 0]}
              />
              <Bar yAxisId="l" dataKey="base" name="Base SN" fill="#1AA6B7" radius={[4, 4, 0, 0]} />
              <Line
                yAxisId="l"
                type="monotone"
                dataKey="das"
                name="DAS"
                stroke={UI.warn}
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Glass>

      {/* Sublimite + alíquota efetiva */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} mt={5}>
        <Glass title="Proximidade do sublimite" sub="acumulado 12 meses vs. tetos">
          {subChart.length === 0 ? (
            <Text fontSize="13px" color={UI.muted}>
              Sem série de sublimite.
            </Text>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={230}>
                <ComposedChart data={subChart}>
                  <defs>
                    <linearGradient id="subFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={limiteColor} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={limiteColor} stopOpacity={0.03} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={UI.border} />
                  <XAxis dataKey="periodo" fontSize={11} stroke={UI.muted} />
                  <YAxis
                    fontSize={11}
                    stroke={UI.muted}
                    width={70}
                    tickFormatter={v => `${(v / 1_000_000).toFixed(1)}M`}
                    domain={[0, (dataMax: number) => Math.max(dataMax, LIMITE_SN)]}
                  />
                  <Tooltip formatter={(v: number) => formatCurrency(v)} />
                  <ReferenceLine
                    y={SUBLIMITE}
                    stroke={UI.warn}
                    strokeDasharray="4 4"
                    label={{
                      value: 'Sublimite 3,6M',
                      fontSize: 10,
                      fill: UI.warn,
                      position: 'insideTopLeft',
                    }}
                  />
                  <ReferenceLine
                    y={LIMITE_SN}
                    stroke={UI.neg}
                    strokeDasharray="4 4"
                    label={{
                      value: 'Limite 4,8M',
                      fontSize: 10,
                      fill: UI.neg,
                      position: 'insideTopLeft',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="acumulado"
                    name="Acumulado 12m"
                    stroke={limiteColor}
                    strokeWidth={2}
                    fill="url(#subFill)"
                  />
                </ComposedChart>
              </ResponsiveContainer>
              <Flex align="center" gap={2} mt={2}>
                <Box
                  flex={1}
                  h="8px"
                  borderRadius="full"
                  bg="rgba(15,23,42,0.08)"
                  overflow="hidden"
                >
                  <Box
                    h="100%"
                    borderRadius="full"
                    bg={limiteColor}
                    w={`${Math.min(pctLimite, 100)}%`}
                  />
                </Box>
                <Text fontSize="12px" fontWeight={800} color={limiteColor}>
                  {pct(pctLimite)} do sublimite
                </Text>
              </Flex>
            </>
          )}
        </Glass>

        <Glass title="Alíquota efetiva por mês" sub="DAS ÷ faturamento">
          {fatChart.length === 0 ? (
            <Text fontSize="13px" color={UI.muted}>
              Sem dados no período.
            </Text>
          ) : (
            <ResponsiveContainer width="100%" height={230}>
              <LineChart data={fatChart}>
                <CartesianGrid strokeDasharray="3 3" stroke={UI.border} />
                <XAxis dataKey="mes" fontSize={11} stroke={UI.muted} />
                <YAxis fontSize={11} stroke={UI.muted} tickFormatter={v => `${v}%`} />
                <Tooltip formatter={(v: number) => pct(v, 2)} />
                <Line
                  type="monotone"
                  dataKey="aliq"
                  name="Alíquota efetiva"
                  stroke="#8B5CF6"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Glass>
      </SimpleGrid>

      {/* Faturamento × base detalhado + divergências */}
      <Box mt={5}>
        <Glass
          title="Faturamento × base do Simples"
          action={
            onOpenAuditoria && resumo.divergencias > 0 ? (
              <Button
                size="xs"
                variant="ghost"
                color={UI.neg}
                rightIcon={<ArrowSquareOut size={13} weight="bold" />}
                onClick={() => onOpenAuditoria('sn')}
              >
                {resumo.divergencias} divergência(s)
              </Button>
            ) : undefined
          }
        >
          <Box border="1px solid" borderColor={UI.border} borderRadius="14px" overflow="hidden">
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg={UI.rowHover}>
                  <Tr>
                    <Th>Mês</Th>
                    <Th isNumeric>Faturamento</Th>
                    <Th isNumeric>Base SN</Th>
                    <Th isNumeric>DAS</Th>
                    <Th isNumeric>Divergência</Th>
                    <Th>Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {fatBase.map(m => (
                    <Tr key={m.mes} _hover={{ bg: UI.rowHover }}>
                      <Td fontWeight={700}>{mesLabel(m.mes)}</Td>
                      <Td isNumeric>{formatCurrency(m.faturamento)}</Td>
                      <Td isNumeric>{formatCurrency(m.base_sn)}</Td>
                      <Td isNumeric>{formatCurrency(m.das)}</Td>
                      <Td isNumeric color={num(m.divergencia) ? UI.neg : UI.muted}>
                        {num(m.divergencia) ? formatCurrency(m.divergencia) : '—'}
                      </Td>
                      <Td>
                        <StatusBadge status={m.status} />
                      </Td>
                    </Tr>
                  ))}
                  {fatBase.length === 0 && (
                    <Tr>
                      <Td colSpan={6} textAlign="center" py={6} color={UI.muted}>
                        Sem dados no período.
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        </Glass>
      </Box>

      {/* DAS x tributos + Anexo I */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} mt={5}>
        <Glass title="DAS × soma de tributos">
          <Box border="1px solid" borderColor={UI.border} borderRadius="14px" overflow="hidden">
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg={UI.rowHover}>
                  <Tr>
                    <Th>Período</Th>
                    <Th isNumeric>Valor DAS</Th>
                    <Th isNumeric>Soma tributos</Th>
                    <Th isNumeric>Divergência</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(data?.das_x_tributos ?? []).map(r => (
                    <Tr key={r.periodo} _hover={{ bg: UI.rowHover }}>
                      <Td fontWeight={700}>{mesLabel(r.periodo)}</Td>
                      <Td isNumeric>{formatCurrency(r.valor_das)}</Td>
                      <Td isNumeric>{formatCurrency(r.soma_tributos)}</Td>
                      <Td isNumeric color={num(r.divergencia) ? UI.neg : UI.muted}>
                        {num(r.divergencia) ? formatCurrency(r.divergencia) : '—'}
                      </Td>
                    </Tr>
                  ))}
                  {(data?.das_x_tributos ?? []).length === 0 && (
                    <Tr>
                      <Td colSpan={4} textAlign="center" py={6} color={UI.muted}>
                        Sem dados no período.
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        </Glass>

        <Glass title="Tabela de alíquotas — Anexo I">
          <Box border="1px solid" borderColor={UI.border} borderRadius="14px" overflow="hidden">
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg={UI.rowHover}>
                  <Tr>
                    <Th>Faixa</Th>
                    <Th isNumeric>RBT12 até</Th>
                    <Th isNumeric>Alíq. nominal</Th>
                    <Th isNumeric>Dedução</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(data?.tabela_aliquotas_anexo_i ?? []).map(r => {
                    const dentro = !!faixaAtual && r.faixa === faixaAtual;
                    return (
                      <Tr
                        key={r.faixa}
                        bg={dentro ? `${UI.brand}10` : undefined}
                        _hover={{ bg: UI.rowHover }}
                      >
                        <Td fontWeight={700}>
                          {r.faixa}
                          {dentro && (
                            <Badge
                              ml={2}
                              style={{ background: `${UI.brand}1A`, color: UI.brand }}
                              borderRadius="full"
                              px={2}
                              fontSize="9.5px"
                              fontWeight={700}
                              textTransform="none"
                            >
                              faixa atual
                            </Badge>
                          )}
                        </Td>
                        <Td isNumeric>{formatCurrency(r.rbt12_ate)}</Td>
                        <Td isNumeric>{pct(r.aliq_nominal, 2)}</Td>
                        <Td isNumeric>{formatCurrency(r.deducao)}</Td>
                      </Tr>
                    );
                  })}
                  {(data?.tabela_aliquotas_anexo_i ?? []).length === 0 && (
                    <Tr>
                      <Td colSpan={4} textAlign="center" py={6} color={UI.muted}>
                        Sem tabela disponível.
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        </Glass>
      </SimpleGrid>
    </Box>
  );
}
